import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Building, Mountain, Sun } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';

const ServiceAreasPage = () => {
  const serviceAreas = {
    'coachella-valley': {
      title: 'Coachella Valley',
      icon: <Sun size={32} />,
      description: 'Luxury resort communities, golf courses, and desert lifestyle destinations',
      cities: [
        { slug: 'palm-springs', name: 'Palm Springs', description: 'Resort city and hospitality hub' },
        { slug: 'palm-desert', name: 'Palm Desert', description: 'Central valley shopping and business district' },
        { slug: 'cathedral-city', name: 'Cathedral City', description: 'Arts community and family neighborhoods' },
        { slug: 'desert-hot-springs', name: 'Desert Hot Springs', description: 'Natural hot springs and wellness destination' },
        { slug: 'indio', name: 'Indio', description: 'City of Festivals and agricultural center' },
        { slug: 'coachella', name: 'Coachella', description: 'City of Eternal Sunshine with diverse community' },
        { slug: 'la-quinta', name: 'La Quinta', description: 'Luxury golf courses and resort community' },
        { slug: 'rancho-mirage', name: 'Rancho Mirage', description: 'Presidential retreat and VIP services' },
        { slug: 'indian-wells', name: 'Indian Wells', description: 'Tennis Garden and international community' },
        { slug: 'thousand-palms', name: 'Thousand Palms', description: 'Coachella Valley Preserve area' },
        { slug: 'sky-valley', name: 'Sky Valley', description: 'Eastern desert community near Salton Sea' },
        { slug: 'mecca', name: 'Mecca', description: 'Small agricultural community in eastern valley' }
      ]
    },
    'hi-desert': {
      title: 'Hi-Desert & Morongo Basin',
      icon: <Mountain size={32} />,
      description: 'Joshua Tree National Park gateway, Marine base, and rural desert communities',
      cities: [
        { slug: 'twentynine-palms', name: 'Twentynine Palms', description: 'Marine Corps base and National Park gateway' },
        { slug: 'yucca-valley', name: 'Yucca Valley', description: 'Central hub of the Morongo Basin' },
        { slug: 'joshua-tree', name: 'Joshua Tree', description: 'National Park gateway and tourism community' },
        { slug: 'morongo-valley', name: 'Morongo Valley', description: 'Western gateway to the Morongo Basin' },
        { slug: 'landers', name: 'Landers', description: 'Rural desert community with unique attractions' },
        { slug: 'pioneertown', name: 'Pioneertown', description: 'Historic Wild West town and music venue destination' }
      ]
    },
    'central-riverside': {
      title: 'Central Riverside County',
      icon: <Building size={32} />,
      description: 'County seat, business hubs, wine country, and major population centers',
      cities: [
        { slug: 'riverside', name: 'Riverside', description: 'County seat and Superior Court hub' },
        { slug: 'moreno-valley', name: 'Moreno Valley', description: 'Major Inland Empire population center' },
        { slug: 'corona', name: 'Corona', description: 'Business hub and northern county gateway' },
        { slug: 'menifee', name: 'Menifee', description: 'Fast-growing central valley community' },
        { slug: 'temecula', name: 'Temecula', description: 'Wine country and business tourism' },
        { slug: 'murrieta', name: 'Murrieta', description: 'Master-planned community with professional demographics' }
      ]
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Service Areas | Legal Document Specialist | Process Server Coverage Southern California"
        description="Comprehensive legal document services across 21 cities in Southern California including Coachella Valley, Hi-Desert, and Central Riverside County. Professional process servers serving all areas."
        keywords="process server service areas, Southern California legal services, Riverside County process server, Coachella Valley legal documents, Hi-Desert legal services"
        canonical="/service-areas"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Our Service Areas
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional legal document services across 21 cities in Southern California. From luxury resort communities to business districts, we provide comprehensive coverage throughout Riverside County and beyond.
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '2rem',
              maxWidth: '600px',
              margin: '2rem auto 0'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--brand-primary)' }}>21</div>
                <div style={{ color: 'var(--text-secondary)' }}>Cities Covered</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--brand-primary)' }}>1.8M+</div>
                <div style={{ color: 'var(--text-secondary)' }}>People Served</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--brand-primary)' }}>3</div>
                <div style={{ color: 'var(--text-secondary)' }}>Major Regions</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Regions */}
      {Object.entries(serviceAreas).map(([regionKey, region]) => (
        <section key={regionKey} style={{ backgroundColor: regionKey === 'hi-desert' ? 'var(--bg-card)' : 'var(--bg-page)', padding: '80px 0' }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                {region.icon}
              </div>
              <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
                {region.title}
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                {region.description}
              </p>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '1.5rem',
              maxWidth: '1200px',
              margin: '0 auto'
            }}>
              {region.cities.map(city => (
                <Link
                  key={city.slug}
                  to={`/cities/${city.slug}`}
                  style={{
                    textDecoration: 'none',
                    backgroundColor: regionKey === 'hi-desert' ? 'var(--bg-page)' : 'var(--bg-card)',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid var(--border-medium)',
                    transition: 'all 0.2s ease',
                    display: 'block'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'var(--border-medium)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <h3 className="heading-3" style={{ color: 'var(--text-primary)', margin: 0 }}>
                      {city.name}
                    </h3>
                    <ArrowRight size={20} color="var(--brand-primary)" />
                  </div>
                  <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
                    {city.description}
                  </p>
                  <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--brand-primary)', fontWeight: '500' }}>
                    View Legal Services →
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Call to Action */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Need Service Outside These Areas?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
              We provide statewide California coverage and can handle legal document services throughout Southern California and beyond.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Anywhere
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Request Coverage Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem', textAlign: 'center' }}>
              Legal Document Services Available
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '3rem', 
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              Professional services available across all 21 service areas
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem'
            }}>
              {[
                { name: 'Process Serving', link: '/process-serving', description: 'Same-day and routine service' },
                { name: 'Court Filing', link: '/court-filing', description: 'E-filing and traditional filing' },
                { name: 'Document Retrieval', link: '/document-retrieval', description: 'Court and medical records' },
                { name: 'Skip Tracing', link: '/skip-tracing', description: 'Legal matter investigations' },
                { name: 'County Recording', link: '/county-recording', description: 'Deeds and legal instruments' },
                { name: 'Attorney Services', link: '/attorney-service', description: 'Full litigation support' }
              ].map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  style={{
                    backgroundColor: 'var(--bg-page)',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-medium)',
                    textDecoration: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    transition: 'transform 0.2s, border-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'var(--border-medium)';
                  }}
                >
                  <h3 className="heading-4" style={{ color: 'var(--brand-primary)' }}>
                    {service.name}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem', textAlign: 'center' }}>
              Helpful Resources for Law Firms
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '3rem', 
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              Comprehensive guides covering California legal procedures and requirements
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              {[
                {
                  title: 'Complete Guide to Process Serving in California',
                  description: 'Learn about legal requirements, registration, service methods, proof of service, timelines, and best practices for 2025.',
                  link: '/resources/process-serving-guide'
                },
                {
                  title: 'Legal Document Services Guide for Law Firms',
                  description: 'Comprehensive overview of all legal document services, outsourcing benefits, and provider selection insights.',
                  link: '/resources/legal-document-services-guide'
                },
                {
                  title: 'California Court Procedures Guide 2025',
                  description: 'Essential filing deadlines, service requirements, motion rules, appeal procedures, and critical 2025 legislative updates.',
                  link: '/resources/california-court-procedures-guide'
                }
              ].map((resource, index) => (
                <Link
                  key={index}
                  to={resource.link}
                  style={{
                    backgroundColor: 'var(--bg-card)',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '2px solid var(--border-medium)',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'transform 0.2s, border-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'var(--border-medium)';
                  }}
                >
                  <h3 className="heading-3" style={{ 
                    marginBottom: '1rem', 
                    color: 'var(--brand-primary)',
                    fontSize: '1.25rem'
                  }}>
                    {resource.title}
                  </h3>
                  <p className="body-medium" style={{ 
                    color: 'var(--text-secondary)', 
                    lineHeight: '1.6',
                    marginBottom: '1rem'
                  }}>
                    {resource.description}
                  </p>
                  <span style={{
                    color: 'var(--brand-primary)',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>
                    Read Complete Guide →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceAreasPage;